import { useEffect, useRef } from 'react';
import { useEddyChat } from '@/lib/useEddyChat';
import { MessageBubble } from './MessageBubble';
import { Composer } from './Composer';
import { StarterChips } from './StarterChips';
import { EddyAvatar } from './EddyAvatar';

interface ChatWindowProps {
  // `page` = roomy full /eddy page; `widget` = compact embedded panel.
  variant?: 'page' | 'widget';
}

export function ChatWindow({ variant = 'page' }: ChatWindowProps) {
  const { messages, send, isStreaming } = useEddyChat();
  const scrollRef = useRef<HTMLDivElement>(null);
  const empty = messages.length === 0;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  return (
    <div className="flex h-full flex-col">
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
        {empty ? (
          <div className="mx-auto flex max-w-xl flex-col items-center gap-4 pt-8 text-center">
            <EddyAvatar size={56} />
            <div>
              <h2 className="text-lg font-semibold text-ink">Hi, I'm Eddy 👋</h2>
              <p className="mt-1 text-sm text-ink-muted">
                I help with EDIBridge — plans, EDI documents, getting set up, and support.
                What can I help you with?
              </p>
            </div>
            <div className="mt-2">
              <StarterChips onPick={send} disabled={isStreaming} />
            </div>
          </div>
        ) : (
          <div className={`mx-auto flex flex-col gap-4 ${variant === 'page' ? 'max-w-2xl' : ''}`}>
            {messages.map((m, i) => (
              <MessageBubble
                key={i}
                message={m}
                streaming={isStreaming && i === messages.length - 1}
              />
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-brand-100 bg-surface-subtle px-4 py-3 sm:px-6">
        <div className={`mx-auto ${variant === 'page' ? 'max-w-2xl' : ''}`}>
          <Composer onSend={send} disabled={isStreaming} />
          <p className="mt-2 text-center text-xs text-ink-muted">
            Eddy answers questions about EDIBridge. For account-specific help, email
            support@edibridge.com.
          </p>
        </div>
      </div>
    </div>
  );
}
